// See more @ https://docs.sentry.io/platforms/javascript

declare global {
    interface Window {
        Sentry?: {
            captureMessage: (
                message: string,
                context?: {
                    level?:
                        'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug'
                    tags?: Record<string, string>
                    extra?: Record<
                        string,
                        string | number | boolean | null | undefined
                    >
                }
            ) => void
        }
    }
}

export type SentryEventLike = {
    exception?: {
        values?: Array<{ value?: string }>
    }
}

export function sentryBeforeSend<T extends SentryEventLike>(
    event: T
): T | null {
    const message = event.exception?.values?.[0]?.value ?? ''

    if (/csrf token mismatch/i.test(message)) {
        return null
    }

    if (/networkerror when attempting to fetch resource/i.test(message)) {
        return null
    }

    if (/failed to fetch dynamically imported module/i.test(message)) {
        return null
    }

    return event
}

class SentryVendor {
    static init: () => Promise<any> = async () => {
        if (!window.shared.sentry.dsn) {
            return await Promise.resolve()
        }

        // Config
        const Sentry = await import('@sentry/browser')
        window.Sentry = Sentry as typeof window.Sentry

        const sampleRate = window.shared.sentry.traces_sample_rate
            ? parseFloat(window.shared.sentry.traces_sample_rate)
            : 0.0

        // Sentry init
        Sentry.init({
            enabled: !!window.shared.sentry.dsn,
            dsn: window.shared.sentry.dsn,
            environment: window.shared.app.env,
            sampleRate,
            integrations: [Sentry.browserTracingIntegration()],
            dataCollection: { userInfo: true },
            denyUrls: [
                /extensions\//i,
                /^chrome-extension:\/\//i,
                /^moz-extension:\/\//i,
            ],
            beforeSend: sentryBeforeSend,
        })

        Sentry.setContext('user', {
            id: window.shared.auth.id,
            name: window.shared.auth.name,
            email: window.shared.auth.email,
        })

        return await Promise.resolve()
    }
}

export default SentryVendor
